import { useMemo, useState } from 'react';
import { AddIcon, PageHeader } from '@billda/design-system';
import { getErrorMessage } from '@billda/shared-ui';
import { Permissions, usePermission } from '@billda/permissions-ui';
import { useSearchParams } from 'react-router-dom';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import ConfirmDialog from '../../../shared/components/ui/ConfirmDialog';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import {
  LABOUR_GROUPS_HELP,
  LABOUR_ITEMS_HELP,
} from '../../../shared/constants/helpContent';
import type { LabourItemGroupDetails } from '../api/labourItemGroupsApi';
import type { LabourItemDefinition } from '../api/labourItemsApi';
import GroupDetailsPanel from '../components/GroupDetailsPanel';
import GroupLifecyclePanel from '../components/GroupLifecyclePanel';
import { ItemDetailsPanel } from '../components/ItemDetailsPanel';
import ItemCreatePanel from '../components/ItemCreatePanel';
import { LabourGroupsListView } from '../components/organisms/LabourGroupsListView';
import { LabourItemsListView } from '../components/organisms/LabourItemsListView';
import { useAvailableLabourItems } from '../hooks/useAvailableLabourItems';
import { useDeleteGroupMutation } from '../hooks/useDeleteGroupMutation';
import { useGroupDetails } from '../hooks/useGroupDetails';
import { useDeleteLabourDefinition } from '../hooks/useLabourDefinitionMutations';
import { useLabourItemGroups } from '../hooks/useLabourItemGroups';
import { useLabourGroupsPanelStore } from '../stores/labourGroupsPanelStore';
import { useLabourItemUIStore } from '../stores/labourItemUIStore';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';

type CatalogueView = 'definitions' | 'groups';

type DeleteTarget =
  | {
      kind: 'item';
      id: string;
      name: string;
      groupCount: number;
    }
  | {
      kind: 'group';
      id: string;
      name: string;
      itemCount: number;
    };

const LabourCataloguePage = () => {
  const canViewDefinitions = usePermission(Permissions.Labour.Edit);
  const canViewGroups = usePermission(Permissions.Labour.Groups.Manage);
  const showToggle = canViewDefinitions && canViewGroups;
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView: CatalogueView =
    searchParams.get('tab') === 'groups' && canViewGroups
      ? 'groups'
      : canViewDefinitions
        ? 'definitions'
        : 'groups';

  const [activeView, setActiveView] = useState<CatalogueView>(initialView);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);

  const { data: definitions = [], isLoading: definitionsLoading } =
    useAvailableLabourItems();
  const { data: groups = [], isLoading: groupsLoading } = useLabourItemGroups();

  const {
    isPanelOpen: isItemPanelOpen,
    panelContent: itemPanelContent,
    selectedItemId,
    openItemCreatePanel,
    openItemDetails,
    openHelp: openItemHelp,
    closePanel: closeItemPanel,
  } = useLabourItemUIStore();
  const {
    isOpen: isGroupPanelOpen,
    contentType: groupContentType,
    groupId: selectedGroupId,
    mode: groupMode,
    openGroupDetails,
    openGroupCreate,
    openHelp: openGroupHelp,
    closePanel: closeGroupPanel,
  } = useLabourGroupsPanelStore();

  const { data: selectedGroup, isLoading: groupDetailLoading } =
    useGroupDetails(selectedGroupId);
  const selectedItem = definitions.find(item => item.id === selectedItemId);

  const {
    mutate: deleteDefinition,
    isPending: deleteDefinitionPending,
    error: deleteDefinitionError,
    reset: resetDefinitionDelete,
  } = useDeleteLabourDefinition();
  const {
    mutate: deleteGroup,
    isPending: deleteGroupPending,
    error: deleteGroupError,
    reset: resetGroupDelete,
  } = useDeleteGroupMutation();

  const isLoading =
    activeView === 'definitions' ? definitionsLoading : groupsLoading;
  const isDeleting = deleteDefinitionPending || deleteGroupPending;
  const isPanelOpen = isItemPanelOpen || isGroupPanelOpen;
  const deleteError =
    deleteTarget?.kind === 'item' ? deleteDefinitionError : deleteGroupError;

  const filteredDefinitions = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? definitions.filter(item => item.name.toLowerCase().includes(term))
      : definitions;
  }, [definitions, search]);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    return term
      ? groups.filter(group => group.name.toLowerCase().includes(term))
      : groups;
  }, [groups, search]);

  const handleTabChange = (value: string) => {
    const nextView = value as CatalogueView;
    setActiveView(nextView);
    setSearch('');
    closeItemPanel();
    closeGroupPanel();
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('tab', nextView === 'groups' ? 'groups' : 'items');
    setSearchParams(nextParams, { replace: true });
  };

  const handleAdd = () => {
    if (activeView === 'definitions') {
      closeGroupPanel();
      openItemCreatePanel();
      return;
    }
    closeItemPanel();
    openGroupCreate();
  };

  const handleHelp = () => {
    if (activeView === 'definitions') {
      closeGroupPanel();
      openItemHelp();
      return;
    }
    closeItemPanel();
    openGroupHelp();
  };

  const handleOpenItem = (itemId: string) => {
    closeGroupPanel();
    openItemDetails(itemId);
  };

  const handleOpenGroup = (groupId: string) => {
    closeItemPanel();
    openGroupDetails(groupId);
  };

  const handleDeleteItem = (item: LabourItemDefinition) => {
    resetDefinitionDelete();
    setDeleteTarget({
      kind: 'item',
      id: item.id,
      name: item.name,
      groupCount: item.groupCount,
    });
  };

  const handleDeleteGroup = (group: LabourItemGroupDetails) => {
    resetGroupDelete();
    setDeleteTarget({
      kind: 'group',
      id: group.id,
      name: group.name,
      itemCount: group.labourItems.length,
    });
  };

  const handleCloseDelete = () => {
    if (isDeleting) {
      return;
    }
    setDeleteTarget(null);
    resetDefinitionDelete();
    resetGroupDelete();
  };

  const handleConfirmDelete = () => {
    if (!deleteTarget) {
      return;
    }
    const onSuccess = () => {
      if (deleteTarget.kind === 'item') {
        closeItemPanel();
      } else {
        closeGroupPanel();
      }
      setDeleteTarget(null);
    };
    if (deleteTarget.kind === 'item') {
      deleteDefinition(deleteTarget.id, { onSuccess });
    } else {
      deleteGroup(deleteTarget.id, { onSuccess });
    }
  };

  const handleClosePanel = () => {
    closeItemPanel();
    closeGroupPanel();
  };

  const panelTitle = (() => {
    if (isItemPanelOpen) {
      if (itemPanelContent === 'item-create') {
        return 'Add labour item';
      }
      if (itemPanelContent === 'item-details') {
        return selectedItem?.name ?? 'Labour item';
      }
      return 'Labour items help';
    }
    if (groupContentType === 'group-details') {
      return selectedGroup?.name ?? 'Group details';
    }
    if (groupContentType === 'group-lifecycle') {
      return groupMode === 'create' ? 'Create group' : 'Edit group';
    }
    return 'Groups help';
  })();

  const deleteMessage = deleteTarget
    ? deleteTarget.kind === 'item'
      ? `This labour item is used by ${deleteTarget.groupCount} group${deleteTarget.groupCount !== 1 ? 's' : ''}. This action cannot be undone.`
      : `This group contains ${deleteTarget.itemCount} labour item${deleteTarget.itemCount !== 1 ? 's' : ''}. This action cannot be undone.`
    : '';

  return (
    <PageContentArea
      isPanelOpen={isPanelOpen}
      panel={
        isPanelOpen ? (
          <>
            <ResizableHandle />
            <SidePanel
              open={isPanelOpen}
              onClose={handleClosePanel}
              title={panelTitle}
              testId={
                itemPanelContent === 'item-details'
                  ? 'labour-item-details-panel'
                  : groupContentType === 'group-details'
                    ? 'labour-group-details-panel'
                    : 'labour-lifecycle-panel'
              }
            >
              {isItemPanelOpen && itemPanelContent === 'item-create' ? (
                <ItemCreatePanel />
              ) : null}
              {isItemPanelOpen &&
              itemPanelContent === 'item-details' &&
              selectedItem ? (
                <ItemDetailsPanel
                  item={selectedItem}
                  onDelete={handleDeleteItem}
                />
              ) : null}
              {isItemPanelOpen && itemPanelContent === 'help' ? (
                <HelpPanel helpContent={LABOUR_ITEMS_HELP} />
              ) : null}
              {isGroupPanelOpen && groupContentType === 'group-details' ? (
                <GroupDetailsPanel
                  group={selectedGroup ?? null}
                  loading={groupDetailLoading}
                  onDelete={handleDeleteGroup}
                />
              ) : null}
              {isGroupPanelOpen && groupContentType === 'group-lifecycle' ? (
                <GroupLifecyclePanel group={selectedGroup ?? null} />
              ) : null}
              {isGroupPanelOpen && groupContentType === 'help' ? (
                <HelpPanel helpContent={LABOUR_GROUPS_HELP} />
              ) : null}
            </SidePanel>
          </>
        ) : undefined
      }
    >
      <PageHeader
        title="Labour"
        helpAction={{ onClick: handleHelp, testId: 'labour-help-button' }}
        actions={
          <PageHeaderAction
            label={
              activeView === 'definitions' ? 'Add labour item' : 'Add group'
            }
            onClick={handleAdd}
            icon={<AddIcon aria-hidden="true" className="size-3.5" />}
            disabled={isLoading || isDeleting}
            data-testid="add-labour-button"
          />
        }
        data-testid="labour-catalogue-header"
      />

      <PageToolbar
        tabs={
          showToggle ? (
            <ChipTabs
              tabs={[
                { label: 'Labour items', value: 'definitions' },
                { label: 'Groups', value: 'groups' },
              ]}
              value={activeView}
              onChange={handleTabChange}
              aria-label="Labour catalogue"
              data-testid="labour-catalogue-tabs"
            />
          ) : undefined
        }
        searchValue={search}
        onSearchChange={setSearch}
        searchMode="inline"
        searchPlaceholder={
          activeView === 'definitions'
            ? 'Search labour items…'
            : 'Search groups…'
        }
        data-testid="labour-catalogue-toolbar"
      />

      <div className="min-h-0 flex-1 overflow-auto">
        {activeView === 'definitions' ? (
          <LabourItemsListView
            items={filteredDefinitions}
            loading={definitionsLoading}
            onItemClick={handleOpenItem}
            disabled={isDeleting}
          />
        ) : (
          <LabourGroupsListView
            groups={filteredGroups}
            loading={groupsLoading}
            onItemClick={handleOpenGroup}
            disabled={isDeleting}
            onAddGroup={openGroupCreate}
          />
        )}
      </div>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={deleteTarget ? `Delete “${deleteTarget.name}”?` : 'Delete'}
        message={deleteMessage}
        onCancel={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        confirmText={
          deleteTarget?.kind === 'group' ? 'Delete group' : 'Delete labour item'
        }
        loading={isDeleting}
        error={deleteError ? getErrorMessage(deleteError) : null}
      />
    </PageContentArea>
  );
};

export default LabourCataloguePage;
